import { createSlice } from '@reduxjs/toolkit';
import { axiosAmApiInstance as amApi } from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import { APIKey, APIKeyState } from '../../@types/apiKey';
import { translator } from 'translation/useTranslator';

const initialState: APIKeyState = {
  loading: {},
  apiKeys: []
};

const slice = createSlice({
  name: 'apiKey',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.loading[action.payload] = true;
    },
    stopLoading(state, action) {
      state.loading[action.payload] = false;
    },
    getAPIKeysSuccess(state, action) {
      state.apiKeys = action.payload;
      state.loading.apiKeys = false;
    },
    removeAPIKey(state, action) {
      state.apiKeys = state.apiKeys.filter((key) => key.id !== action.payload);
    }
  }
});

// Reducer
export default slice.reducer;

// Actions

export function getAPIKeys() {
  return async () => {
    dispatch(slice.actions.startLoading('apiKeys'));
    try {
      const response = await amApi.get('/api-keys');
      dispatch(slice.actions.getAPIKeysSuccess(response.data.apiKeys));
      dispatch(slice.actions.stopLoading('apiKeys'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.apiKeysFetched() ?? 'API keys fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('apiKeys'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchAPIKeys() ?? 'Could not fetch API keys'
      });
    }
  };
}

export function deleteAPIKey(id: number) {
  return async () => {
    dispatch(slice.actions.startLoading('apiKeys'));
    try {
      dispatch(slice.actions.removeAPIKey(id));
      await amApi.delete('/api-keys/' + id);
      dispatch(getAPIKeys());

      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.apiKeyDeletedWithId(`${id}`) ?? `API key with ID ${id} deleted`
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotDeleteAPIKeyWithId(`${id}`) ??
          `Could not delete API key with ID ${id}`
      });
    }
  };
}

export function createAPIKey(name: string) {
  return async () => {
    dispatch(slice.actions.startLoading('apiKeys'));
    try {
      await amApi.post('/api-keys', { name });
      dispatch(getAPIKeys());

      return await Promise.resolve({
        defaultSuccessMessage: translator?.apiKeyCreatedWithName(name) ?? `API key ${name} created`
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotCreateAPIKeyWithName(name) ??
          `Could not create API key with name ${name}`
      });
    }
  };
}

type EditAPIKeyProps = {
  apiKey: APIKey;
};
export function editAPIKey({ apiKey }: EditAPIKeyProps) {
  return async () => {
    try {
      await amApi.put('/api-keys/' + apiKey.id, {
        name: apiKey.name,
        active: apiKey.active
      });
      dispatch(getAPIKeys());
      return await Promise.resolve({
        defaultSuccessMessage: translator?.apiKeyUpdated() ?? 'API key updated'
      });
    } catch (error: any) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateAPIKey() ?? 'Could not update API key'
      });
    }
  };
}
